import React, { useEffect, useState } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Alert, FormText } from 'reactstrap'
import { useMutation } from '@apollo/client'
import { CREATE_MESSAGE_TEMPLATE, UPDATE_MESSAGE_TEMPLATE } from './Mutations'

export default function CreateEditMessageTemplateModal({ isOpen, toggle, template, onSuccess }) {
    const isEdit = Boolean(template)
    const [title, setTitle] = useState('')
    const [shortcut, setShortcut] = useState('')
    const [content, setContent] = useState('')
    const [isActive, setIsActive] = useState(true)
    const [errors, setErrors] = useState([])

    useEffect(() => {
        if (isOpen) {
            setTitle(template?.title || '')
            setShortcut(template?.shortcut || '')
            setContent(template?.content || '')
            setIsActive(template?.isActive ?? true)
            setErrors([])
        }
    }, [isOpen, template])

    const [createTemplate, { loading: creating }] = useMutation(CREATE_MESSAGE_TEMPLATE, {
        onCompleted: (data) => {
            const response = data?.messagingCreateMessageTemplate
            if (response?.ok) {
                if (onSuccess) {
                    onSuccess('Template created successfully.')
                }
                toggle()
            } else {
                setErrors(response?.errors || ['Unable to create template.'])
            }
        },
        onError: (error) => {
            setErrors([error.message])
        }
    })

    const [updateTemplate, { loading: updating }] = useMutation(UPDATE_MESSAGE_TEMPLATE, {
        onCompleted: (data) => {
            const response = data?.messagingUpdateMessageTemplate
            if (response?.ok) {
                if (onSuccess) {
                    onSuccess('Template updated successfully.')
                }
                toggle()
            } else {
                setErrors(response?.errors || ['Unable to update template.'])
            }
        },
        onError: (error) => {
            setErrors([error.message])
        }
    })

    const handleSubmit = (e) => {
        e.preventDefault()
        setErrors([])

        const trimmedTitle = title.trim()
        const trimmedContent = content.trim()

        if (!trimmedTitle) {
            setErrors(['Title is required.'])
            return
        }
        if (!trimmedContent) {
            setErrors(['Content is required.'])
            return
        }

        const variables = {
            title: trimmedTitle,
            content: trimmedContent,
            shortcut: shortcut.trim() || null,
            isActive,
        }

        if (isEdit) {
            updateTemplate({
                variables: {
                    ...variables,
                    id: template.id,
                }
            })
        } else {
            createTemplate({
                variables
            })
        }
    }

    const loading = creating || updating

    return (
        <Modal isOpen={isOpen} size="lg">
            <ModalHeader>
                {isEdit ? 'Edit Template' : 'Create Template'}
            </ModalHeader>
            <Form onSubmit={handleSubmit}>
                <ModalBody>
                    {errors.length > 0 && (
                        <Alert color="danger">
                            {errors.map((error, index) => (
                                <div key={index}>{error}</div>
                            ))}
                        </Alert>
                    )}
                    <FormGroup>
                        <Label for="templateTitle">Title *</Label>
                        <Input
                            id="templateTitle"
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="e.g. Greeting"
                            disabled={loading}
                            required
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label for="templateShortcut">Shortcut</Label>
                        <Input
                            id="templateShortcut"
                            type="text"
                            value={shortcut}
                            onChange={(e) => setShortcut(e.target.value)}
                            placeholder="Optional quick command, e.g. /greeting"
                            disabled={loading}
                        />
                        <FormText color="muted">
                            Agents can type this shortcut in the composer to quickly insert the template.
                        </FormText>
                    </FormGroup>
                    <FormGroup>
                        <Label for="templateContent">Content *</Label>
                        <Input
                            id="templateContent"
                            type="textarea"
                            rows="6"
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            placeholder="Write the canned response..."
                            disabled={loading}
                            required
                        />
                    </FormGroup>
                    <FormGroup check>
                        <Label check>
                            <Input
                                type="checkbox"
                                checked={isActive}
                                onChange={(e) => setIsActive(e.target.checked)}
                                disabled={loading}
                            />{' '}
                            Mark template as active
                        </Label>
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggle} disabled={loading}>
                        Cancel
                    </Button>
                    <Button color="primary" type="submit" disabled={loading}>
                        {loading ? 'Saving…' : isEdit ? 'Update Template' : 'Create Template'}
                    </Button>
                </ModalFooter>
            </Form>
        </Modal>
    )
}
